/**
 * Entry-point detection for the mobile-to-web SSO bridge.
 *
 * A native app hands off to this web app by having the IDP redeem a one-time
 * bridge ticket, which sets the IDP SSO session cookie and redirects here. This
 * app never sees the ticket — it only sees a marker telling it to attempt a
 * silent (`prompt=none`) OAuth2 login as its own client.
 *
 * Two ways in:
 *   - `?sso_bridge=true` on any URL — land on the page, log in silently.
 *   - `/sso/bridge?target=/my_bookings` — a single landing URL to hand the IDP,
 *     carrying where to go afterwards.
 */
import { ROUTE_SSO_BRIDGE } from '../components/Root/Constants';

/**
 * The URL the browser arrived on, captured at module load.
 *
 * It cannot be read later: the router's `<Redirect exact from="/" to=…>` rebuilds
 * the location from a bare path and drops the query string, and the bridge only
 * runs once discovery has come back over the network — long after that. Reading
 * `window.location` at that point would miss the marker entirely.
 */
const entryLocation =
    typeof window === 'undefined'
        ? { pathname: '', search: '' }
        : { pathname: window.location.pathname, search: window.location.search };

/** Query parameter marking a URL as a bridge landing. */
export const SSO_BRIDGE_PARAM = 'sso_bridge';

/** Query parameter carrying the post-login destination on {@link ROUTE_SSO_BRIDGE}. */
export const SSO_BRIDGE_TARGET_PARAM = 'target';

/**
 * A same-origin path: starts with a single `/`, no whitespace. `//evil.com` and
 * `/\evil.com` are protocol-relative URLs to a browser, not paths — following
 * one would turn this bridge into an open redirect.
 */
const SAFE_TARGET = /^\/(?![/\\])\S*$/;

/**
 * Whether a post-login destination may be navigated to.
 *
 * @param {unknown} target Candidate destination, as received from the URL.
 * @returns {boolean} true when `target` is a same-origin path.
 */
export function isSafeTarget(target) {
    return typeof target === 'string' && SAFE_TARGET.test(target);
}

/** Rebuild the current URL without the bridge marker, so it can be returned to. */
function stripBridgeMarker(pathname, search) {
    const params = new URLSearchParams(search);
    params.delete(SSO_BRIDGE_PARAM);
    const rest = params.toString();
    return rest ? `${pathname}?${rest}` : pathname;
}

/**
 * The URL the browser arrived on, before any router navigation.
 *
 * @returns {{ pathname: string, search: string }} the captured location.
 */
export function getEntryLocation() {
    return entryLocation;
}

let navigationInFlight = false;

/**
 * Record that a navigation away from this document has been started.
 *
 * `window.location.assign` does not unmount anything: the current document goes
 * on running until the new one commits. So nothing can infer "we are leaving"
 * from React alone, and a timer meant to catch a stalled bridge would happily
 * cancel a login that was simply slow.
 *
 * @returns {void}
 */
export function markNavigationStarted() {
    navigationInFlight = true;
}

/**
 * Whether a navigation away from this document has been started.
 *
 * @returns {boolean}
 */
export function isNavigationInFlight() {
    return navigationInFlight;
}

/**
 * Compare a pathname the way the router matched it.
 *
 * `<Route exact>` implies `strict: false`, so react-router mounts the bridge
 * page for `/sso/bridge/` too. Comparing the raw string would leave that load
 * with the page rendered and the saga never started — a 20s loader, then the
 * watchdog, with the IDP cookie and the OAuth2 config both perfectly fine.
 */
function isRoute(pathname, route) {
    const trimmed = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
    return trimmed.toLowerCase() === route.toLowerCase();
}

/**
 * Decide whether this page load is a bridge entry.
 *
 * Yields to the legacy native handoff: when the URL still carries a
 * `UserToken` / `refreshToken` deep link, that flow owns this load and running
 * both would race on the same mount.
 *
 * @param {{ pathname: string, search: string }} location Current location.
 * @returns {{ returnTo: string|undefined, isDedicatedRoute: boolean }|null}
 *   the bridge entry, or `null` when this is an ordinary page load.
 */
export function detectBridgeEntry(location) {
    const { pathname, search } = location || {};
    if (typeof pathname !== 'string') return null;

    const params = new URLSearchParams(search || '');
    if (params.has('UserToken') || params.has('refreshToken')) return null;

    if (isRoute(pathname, ROUTE_SSO_BRIDGE)) {
        const target = params.get(SSO_BRIDGE_TARGET_PARAM);
        return { returnTo: isSafeTarget(target) ? target : undefined, isDedicatedRoute: true };
    }

    if (params.get(SSO_BRIDGE_PARAM) === 'true') {
        const returnTo = stripBridgeMarker(pathname, search || '');
        // A hostile pathname (`//evil.com?sso_bridge=true`) reaches us as a
        // "path" that browsers resolve as protocol-relative. Validated here as
        // well as at the navigation sites, so the invariant does not rest on a
        // single downstream call.
        return { returnTo: isSafeTarget(returnTo) ? returnTo : undefined, isDedicatedRoute: false };
    }

    return null;
}
